/** Longest side of an uploaded photo: enough to read the card number, small enough for a quick mobile upload. */
export const MAX_IMAGE_SIDE = 2000;

/** What the server stores (it checks the content too). */
const UPLOAD_TYPES = /^image\/(jpeg|png|webp)$/;

/** A photo the server takes: JPEG, PNG or WebP. Its size doesn't matter, resizeImage brings it down. */
export const isPhotoType = (file: Blob) => UPLOAD_TYPES.test(file.type);

/**
 * Downscales a photo to MAX_IMAGE_SIDE (respecting EXIF rotation): every upload goes through it, so a
 * 48 MP phone photo (often over the server's 8 MB per photo) arrives as a few hundred KB, as JPEG.
 * `alpha` (a logo) encodes WebP instead, which keeps transparency (a browser that can't encode WebP
 * hands back PNG; the server takes both). A photo already small enough in a type the server takes goes
 * as it is (no second compression; the server applies its EXIF rotation and drops the rest), unless
 * `reencode` (an upload stored as sent, whose EXIF, GPS included, would go public). Falls back to the
 * original file when the browser cannot decode it (e.g. HEIC on desktop Chrome) or lacks createImageBitmap.
 */
export async function resizeImage(file: Blob, { maxSide = MAX_IMAGE_SIDE, alpha = false, reencode = false }: { maxSide?: number; alpha?: boolean; reencode?: boolean } = {}): Promise<Blob> {
  if (typeof createImageBitmap !== "function") return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && isPhotoType(file) && !reencode) {
      bitmap.close();
      return file;
    }
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, alpha ? "image/webp" : "image/jpeg", 0.9));
    return blob && blob.size > 0 ? blob : file;
  } catch {
    return file;
  }
}
